/**
 * A small, safe markdown renderer for the Omega Wolf Guide: headings, paragraphs,
 * bullet and numbered lists, tables, code blocks, `code`, **bold**, and links.
 * Builds React elements only (never HTML). External links go through the chat
 * formatter (http(s) only, new tab); `[text](mailto:…)` opens the mail app;
 * `[text](slug)` links to another Guide page call `onPage`.
 */
import React from 'react';
import { formatInline } from '../components/chatText';

const INTERNAL = /\[([^\]\n]{1,200})\]\((?:mailto:([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})|([a-z0-9][a-z0-9-]{0,80})(?:\.md)?)\)/g;
const CODE = /`([^`\n]{1,200})`/g;

function inline(text: string, key: string, onPage?: (slug: string) => void): React.ReactNode[] {
  // Split on internal links, then code spans, then hand the rest to the chat formatter.
  const out: React.ReactNode[] = [];
  let last = 0;
  INTERNAL.lastIndex = 0;
  let m: RegExpExecArray | null;
  const rest = (s: string, k: string) => {
    let l = 0;
    CODE.lastIndex = 0;
    let c: RegExpExecArray | null;
    while ((c = CODE.exec(s)) !== null) {
      if (c.index > l) out.push(...formatInline(s.slice(l, c.index), `${k}-t${l}`));
      out.push(<code key={`${k}-c${c.index}`} className="ow-md-code">{c[1]}</code>);
      l = c.index + c[0].length;
    }
    if (l < s.length) out.push(...formatInline(s.slice(l), `${k}-t${l}`));
  };
  while ((m = INTERNAL.exec(text)) !== null) {
    if (m.index > last) rest(text.slice(last, m.index), `${key}-${last}`);
    const email = m[2];
    const slug = m[3];
    out.push(
      email ? (
        <a key={`${key}-m${m.index}`} href={`mailto:${email}`}>
          {m[1]}
        </a>
      ) : onPage ? (
        <button key={`${key}-l${m.index}`} type="button" className="ow-md-link" onClick={() => onPage(slug)}>
          {m[1]}
        </button>
      ) : (
        m[1]
      ),
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) rest(text.slice(last), `${key}-${last}`);
  return out;
}

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

export function renderMarkdown(src: string, onPage?: (slug: string) => void): React.ReactNode[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: React.ReactNode[] = [];
  let i = 0;
  let n = 0;
  const key = () => `md-${n++}`;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (line.startsWith('```')) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) code.push(lines[i++]);
      i += 1;
      out.push(<pre key={key()} className="ow-md-pre">{code.join('\n')}</pre>);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const k = key();
      const level = Math.min(h[1].length + 1, 5);
      const Tag = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5';
      out.push(<Tag key={k} className={`ow-md-h${h[1].length}`}>{inline(h[2], k, onPage)}</Tag>);
      i += 1;
      continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) rows.push(cells(lines[i++]));
      const k = key();
      out.push(
        <div key={k} className="ow-md-table-wrap">
          <table className="ow-md-table">
            <thead>
              <tr>{head.map((c, j) => <th key={j}>{inline(c, `${k}-h${j}`, onPage)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, j) => <td key={j}>{inline(c, `${k}-${ri}-${j}`, onPage)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const bullet = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(line);
    if (bullet) {
      const ordered = /\d+\./.test(bullet[2]);
      const items: string[] = [];
      while (i < lines.length) {
        const b = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(lines[i]);
        if (b && /\d+\./.test(b[2]) === ordered) items.push(b[3]);
        else if (lines[i].startsWith('  ') && lines[i].trim() && items.length) items[items.length - 1] += ` ${lines[i].trim()}`;
        else break;
        i += 1;
      }
      const k = key();
      const List = ordered ? 'ol' : 'ul';
      out.push(<List key={k} className="ow-md-list">{items.map((it, j) => <li key={j}>{inline(it, `${k}-${j}`, onPage)}</li>)}</List>);
      continue;
    }
    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) quote.push(lines[i++].replace(/^>\s?/, ''));
      const k = key();
      out.push(<blockquote key={k} className="ow-md-quote">{inline(quote.join(' '), k, onPage)}</blockquote>);
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length && lines[i].trim() && !/^(#{1,4}\s|```|>|\s*([-*]|\d+\.)\s)/.test(lines[i]) && !isTableRow(lines[i])
    ) {
      para.push(lines[i++].trim());
    }
    if (!para.length) {
      para.push(lines[i++].trim());
    }
    const k = key();
    out.push(<p key={k} className="ow-md-p">{inline(para.join(' '), k, onPage)}</p>);
  }
  return out;
}
